import React, { useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Grid, 
  User, 
  Settings, 
  HelpCircle, 
  LogOut, 
  GraduationCap, 
  ChevronRight,
  X,
  School,
  SquareDashedKanban,
  ReceiptText,
  BookOpenText,
  TextQuote
} from 'lucide-react';
import api from '../../connector/api';

export default function Sidebar({ currentUser, setCurrentUser, isOpen, onClose }) {
  const isStudent = currentUser?.role === 'student';
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch logged-in user context
  useEffect(() => {
    if (!currentUser && typeof setCurrentUser === 'function') {
      const fetchUserData = async () => {
        try {
          // FIXED: Swapped endpoint path to target the correct user payload route
          const response = await api.get('accounts/me/');
          if (response.data && response.data.user) {
            // EXTRACT NESTED USER: target the specific user block inside the response
            setCurrentUser(response.data.user);
          }
        } catch (error) {
          console.error("Could not load user data from backend API:", error);
        }
      };
      fetchUserData();
    }
  }, [currentUser, setCurrentUser]);

  const displayName = currentUser?.first_name 
    ? `${currentUser.last_name} ${currentUser.first_name || ''}`.trim()
    : (currentUser?.username || 'Loading...');

  const displayUsername = currentUser?.username 
    ? `@${currentUser.username}` 
    : 'Loading...';

  const displayAvatar = currentUser?.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=100&auto=format&fit=crop';

  const mainNavItems = [
    { id: 'overview', label: 'Academic Overview', icon: SquareDashedKanban , path: '/academic' },
    { id: 'section', label: 'Class Section', icon: School , path: '/academic/class-section' },
    { id: 'subject', label: 'Subjects', icon: ReceiptText, path: '/academic/subject' },
    { id: 'assign subject', label: 'Assign Subject', icon: TextQuote , path: '/academic/subject/assign' },
    { id: 'classroom', label: 'Classroom', icon: BookOpenText , path: '/academic/classroom' },
  ];
  const secondaryNavItems = [
    { id: 'settings', label: 'Settings', icon: Settings, path: '/settings' },
    { id: 'help', label: 'Support & Help', icon: HelpCircle, path: '/support' },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (typeof onClose === 'function') onClose();
  };

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = "/login";
  };

  const sidebarContent = (
    <div className="w-64 bg-[#002855] bg-opacity-80 text-slate-200 border-r border-white/10 flex flex-col justify-between shrink-0 h-full font-dynamic-title select-none">
      <div>
        {/* Branding header */}
        <div className="h-16 px-6 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 border border-white/20 rounded-xl text-white shadow-inner">
              <GraduationCap className="h-5 w-5" />
            </div>
            <div>
              <span className="font-bold text-sm tracking-wider text-white block">
                ACADEMIC
              </span>
              <span className="text-[9px] font-extrabold tracking-[0.2em] block text-sky-400/80 uppercase truncate max-w-[180px]">
                {/* Targets the injected backend group value, falling back cleanly to 'Workspace' */}
                {currentUser?.group_name || 'Workspace'}
              </span>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="lg:hidden p-1 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors">
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Navigation Area */}
        <div className="px-4 py-6">
          <p className="px-3 text-[10px] font-bold text-white/40 uppercase tracking-widest mb-3">
            Platform
          </p>
          <nav className="space-y-1">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-medium tracking-wide transition-all duration-200 group cursor-pointer ${
                    isActive
                      ? 'bg-white text-[#002853] shadow-lg shadow-black/10 font-semibold'
                      : 'hover:bg-white/5 hover:text-white text-white/70'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className={`h-4 w-4 shrink-0 transition-colors ${isActive ? 'text-[#002853]' : 'text-white/50 group-hover:text-white'}`} />
                    <span>{item.label}</span>
                  </div>
                  {isActive && <ChevronRight className="h-3 w-3 text-[#002853]" />}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Profile Footer Panel */}
      <div>
        <div className="px-4 py-2 border-t border-white/5">
          <nav className="space-y-1 pt-2 pb-1">
            {secondaryNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium tracking-wide transition-all cursor-pointer ${
                    isActive ? 'bg-white/10 text-white font-semibold' : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <Icon className="h-4 w-4 text-white/40 group-hover:text-white" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-4 border-t border-white/10 bg-black/10 backdrop-blur-xs flex flex-col gap-3">
          <div className="flex items-center gap-3 px-2">
            <img
              src={displayAvatar}
              alt={displayName}
              className="w-9 h-9 rounded-xl object-cover border border-white/20 ring-2 ring-black/20"
              referrerPolicy="no-referrer"
            />
            <div className="min-w-0 flex-1">
              <h4 className="text-xs font-bold text-white truncate">{displayName}</h4>
              <p className="text-[10px] font-medium text-white/40 truncate mt-0.5">
                {displayUsername}
              </p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs font-semibold text-rose-300 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 transition-all duration-200 cursor-pointer"
          >
            <LogOut className="h-4 w-4 text-rose-400" />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* 1. Mobile backdrop overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-40 lg:hidden transition-opacity duration-300" 
          onClick={onClose} 
        />
      )}

      {/* 2. Responsive Side Drawer Shell */}
      {/* FIXED: Handled width collapsing cleanly via dynamic classes instead of forced DOM removal */}
      <aside className={`fixed inset-y-0 left-0 z-50 lg:static lg:block transform transition-all duration-300 ease-out h-screen ${
        isOpen ? 'translate-x-0 w-64' : '-translate-x-full lg:translate-x-0 lg:w-64'
      }`}>
        {sidebarContent}
      </aside>
    </>
  );
}